import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal,
  TemplateRef,
  viewChild,
} from "@angular/core";
import { Router } from "@angular/router";
import {
  AccountMarketplace,
  AlgoMode,
  AudienceTargeting,
  CampaignType,
  SdStrategyCreateRequest,
  StrategyStateEnum,
  StrategyTactic,
  TacticType,
} from "@front/m19-api-client";
import { SdTargetingType } from "@front/m19-models";
import { Constant, SdStrategyService, TacosStrategiesService, TacosStrategy } from "@front/m19-services";
import {
  IAlertComponent,
  IButtonDirective,
  IIconComponent,
  IToggleComponent,
  ITooltipDirective,
  ModalRef,
  ModalService,
  ToasterService,
} from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { SbCreativeCardComponent } from "@m19-board/shared/sb-creative-card.component";
import { SdTargetingFormComponent } from "@m19-board/shared/sd-targeting-form/sd-targeting-form.component";
import { SdTargetingFormModel } from "@m19-board/shared/sd-targeting-form/sd-targeting-form.types";
import { SdStrategyTargetingListComponent } from "@m19-board/strategies/strategy-page/strategy-page-sd-targetings/sd-strategy-targeting-list.component";
import { switchMap } from "rxjs";

const TACOS_STRATEGIES_URL = "/advertising/tacos-strategies";

@Component({
  selector: "app-tacos-ad-types",
  imports: [
    TranslocoDirective,
    IToggleComponent,
    ITooltipDirective,
    SbCreativeCardComponent,
    IButtonDirective,
    IAlertComponent,
    IIconComponent,
    SdTargetingFormComponent,
    SdStrategyTargetingListComponent,
  ],
  templateUrl: "./tacos-ad-types.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TacosAdTypesComponent {
  readonly CampaignType = CampaignType;

  private readonly tacosStrategiesService = inject(TacosStrategiesService);
  private readonly toastrService = inject(ToasterService);
  private readonly translocoService = inject(TranslocoService);
  private readonly modalService = inject(ModalService);
  private readonly sdStrategyService = inject(SdStrategyService);
  private readonly router = inject(Router);

  sdTargetingModal = viewChild<TemplateRef<any>>("sdTargetingModal");
  modalRef: ModalRef<any, any> | undefined;

  am = input.required<AccountMarketplace>();
  tacosStrategy = input.required<TacosStrategy>();
  sbAsins = input<string[] | undefined>();
  missingAsinsInSb = input<string[] | undefined>();
  isReadonly = input.required<boolean>();

  sbStrategyEx = computed(() => (this.tacosStrategy().sbStrategy ? this.tacosStrategy().sbStrategy! : undefined));
  sdStrategyEx = computed(() => (this.tacosStrategy().sdStrategy ? this.tacosStrategy().sdStrategy! : undefined));
  canCreateNewCreative = computed(
    () =>
      !this.tacosStrategy().sbCreatives?.length ||
      this.tacosStrategy().sbCreatives!.length < Constant.maxCreativesPerStrategy,
  );

  sdTargetingFormValue = signal<SdTargetingFormModel | undefined>(undefined);
  sdLoadingState = signal(false);
  sbLoadingState = signal(false);
  isTacosStrategyPaused = computed(() => this.tacosStrategy().state === StrategyStateEnum.PAUSED);
  isSdToggleChecked = computed(
    () =>
      this.tacosStrategy().sdStrategy &&
      this.tacosStrategy().sdSpendRepartition !== undefined &&
      this.tacosStrategy().sdSpendRepartition! > 0,
  );

  isSbToggleChecked = computed(
    () =>
      this.tacosStrategy().sbStrategy &&
      this.tacosStrategy().sbSpendRepartition !== undefined &&
      this.tacosStrategy().sbSpendRepartition! > 0,
  );

  onSdToggleChange(checked: boolean) {
    if (checked && !this.tacosStrategy().sdStrategy) {
      this.openSdTargetingForm();
    } else {
      const newState = checked ? StrategyStateEnum.ENABLED : StrategyStateEnum.PAUSED;
      this.updateStrategyState(CampaignType.SD, newState);
    }
  }

  onSbToggleChange(checked: boolean) {
    if (checked && !this.tacosStrategy().sbStrategy) {
      this.openSbForm();
    } else {
      const newState = checked ? StrategyStateEnum.ENABLED : StrategyStateEnum.PAUSED;
      this.updateStrategyState(CampaignType.SB, newState);
    }
  }
  openSbForm() {
    if (!this.canCreateNewCreative()) return;

    const tacosId = this.tacosStrategy().tacosStrategyGroupId;
    if (this.sbStrategyEx()) {
      void this.router.navigate([TACOS_STRATEGIES_URL, tacosId, "sb", "new-ad-line"]);
      return;
    }

    void this.router.navigate([TACOS_STRATEGIES_URL, tacosId, "sb"]);
  }

  openSdTargetingForm() {
    this.modalRef = this.modalService.openModal(this.sdTargetingModal()!, {
      modalTitle: `New Targeting - ${this.tacosStrategy().spStrategy!.name}`,
      closeMode: "buttonOnly",
      size: "lg",
    });
  }

  public activateSdStrategy() {
    const strategy: SdStrategyCreateRequest = {
      state: StrategyStateEnum.ENABLED,
      name: this.tacosStrategy().spStrategy!.name!,
      asins: this.tacosStrategy().spStrategy!.asins!.map((a) => a.asin),
      algoMode: AlgoMode.TACOS_TARGET,
      disableOtherQueries: this.sdTargetingFormValue()!.targetingType !== SdTargetingType.PRODUCT_AI_TARGETING,
      audienceTargetings: this.getAudienceTargeting(this.sdTargetingFormValue()!.targetingType!),
      tacticSegmentId: this.getTactics(this.sdTargetingFormValue()!.targetingType!)?.segmentId,
    };

    this.sdLoadingState.set(true);
    this.sdStrategyService
      .createStrategy(strategy)
      .pipe(
        switchMap((strategy) =>
          this.tacosStrategiesService.updateTacosStrategyGroup({
            accountId: this.am()!.accountId,
            marketplace: this.am()!.marketplace,
            tacosStrategyGroupId: this.tacosStrategy()!.tacosStrategyGroupId!,
            sdStrategyId: strategy.strategyId!,
          }),
        ),
      )
      .subscribe({
        next: () => {
          this.toastrService.success(this.translocoService.translate("tacos-strategy.sd_strategy_activated"));
          this.sdLoadingState.set(false);
          this.modalRef?.close();
        },
        error: (err) => {
          this.toastrService.error(err);
          this.sdLoadingState.set(false);
        },
      });
  }

  private updateStrategyState(campaignType: CampaignType, state: StrategyStateEnum) {
    if (campaignType === CampaignType.SD) {
      this.sdLoadingState.set(true);
    } else if (campaignType === CampaignType.SB) {
      this.sbLoadingState.set(true);
    }

    this.tacosStrategiesService
      .updateTacosStrategyGroup({
        accountId: this.am()!.accountId,
        marketplace: this.am()!.marketplace,
        tacosStrategyGroupId: this.tacosStrategy()!.tacosStrategyGroupId!,
        campaignType,
        state,
      })
      .subscribe(() => {
        this.toastrService.success(this.translocoService.translate("tacos-strategy.strategy_state_updated"));
        if (campaignType === CampaignType.SD) {
          this.sdLoadingState.set(false);
        } else if (campaignType === CampaignType.SB) {
          this.sbLoadingState.set(false);
        }
      });
  }

  private getTactics(targetingType: SdTargetingType): StrategyTactic | undefined {
    if (targetingType === SdTargetingType.PRODUCT) {
      return { tacticType: TacticType.LEGACY, segmentId: this.sdTargetingFormValue()!.segment!.segmentId };
    }
    return undefined;
  }

  private getAudienceTargeting(targetingType: SdTargetingType): AudienceTargeting[] {
    if (!this.sdTargetingFormValue()) return [];
    if (targetingType === SdTargetingType.REMARKETING) {
      return [
        {
          expressionType: this.sdTargetingFormValue()!.audienceExpressionType!,
          matchType: this.sdTargetingFormValue()!.audienceMatchType!,
          lookback: this.sdTargetingFormValue()!.audienceLookback!,
        },
      ];
    }
    return [];
  }
}
